import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, User, Phone, MapPin, IdCard, FolderKanban, Wallet, History, StickyNote, Paperclip, FileText,
  Image as ImageIcon, Film, Mic, Star, ClipboardList, ExternalLink, AlertTriangle, Loader2, Clock, Sparkles, RefreshCw,
  UserPlus, Link2, Pencil, Check, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { whatsappAPI } from './whatsappAPI';
import ClientQuickViewModal from '../../components/ClientQuickViewModal';
import { situacaoInfo, Chip, telefoneConversa, ehLid, numeroInformado } from './organizacao';
import ContatoClienteModal from './ContatoClienteModal';
import { HistoricoModal } from './HistoricoAtendimento';

/* Painel do cliente ao lado da conversa: quem é, o que tem em andamento com a empresa e o que já aconteceu.
 * O destaque visual é o avanço dos projetos (etapas como segmentos); o resto fica discreto. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (id) => `${API}/whatsapp/media/${id}?token=${encodeURIComponent(localStorage.getItem('agrolink_token') || '')}`;
export const fmtProtocolo = (p) => (p ? (String(p).startsWith('#') ? p : `#${p}`) : '');
// mesmas rotas usadas no resto do sistema (o projeto abre expandido na lista; o serviço abre destacado)
export const linkProjeto = (p) => `${p.status === 'arquivado' ? '/projetos/arquivados' : p.status === 'suspenso' ? '/projetos/suspensos' : '/projetos'}?project=${p.id}`;
export const linkServico = (s) => `/financeiro/servicos?svc=${s.id}`;
const btnLink = 'h-8 px-3 rounded-lg border text-[13px] font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800';
const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const data = (iso, comAno = true) => {
  if (!iso) return '—';
  const d = new Date(String(iso).length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', ...(comAno ? { year: '2-digit' } : {}) });
};
const fmtCpf = (c = '') => {
  const d = c.replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return c;
};
const duracao = (seg) => {
  if (seg == null) return null;
  const m = Math.round(seg / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h${String(m % 60).padStart(2, '0')}` : `${Math.floor(h / 24)}d ${h % 24}h`;
};
const desde = (iso) => (iso ? duracao((Date.now() - Date.parse(iso)) / 1000) : null);

function Secao({ icon: Icon, titulo, extra, children, testid }) {
  return (
    <section className="px-5 py-4 border-t border-slate-100 dark:border-slate-800" data-testid={testid}>
      <h4 className="flex items-center gap-2 text-[13px] font-semibold text-slate-700 dark:text-slate-200 mb-2.5">
        <Icon className="w-4 h-4 text-slate-400" />{titulo}
        {extra != null && <span className="ml-auto text-xs font-normal text-slate-400">{extra}</span>}
      </h4>
      {children}
    </section>
  );
}

/* etapas do projeto como segmentos: a peça que se lê de longe */
function Etapas({ pos, total, suspenso }) {
  if (!pos || !total) return null;
  const cor = suspenso ? 'bg-amber-400' : 'bg-emerald-600';
  return (
    <div className="flex gap-[3px] mt-2" role="img" aria-label={`Etapa ${pos} de ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`h-1.5 flex-1 rounded-full ${i < pos ? cor : 'bg-slate-200 dark:bg-slate-700'} ${i === pos - 1 ? 'ring-2 ring-offset-1 ring-emerald-600/30 dark:ring-offset-slate-900' : ''}`} />
      ))}
    </div>
  );
}

function iconeArquivo(a) {
  if (a.message_type === 'image' || a.message_type === 'sticker') return ImageIcon;
  if (a.message_type === 'video') return Film;
  if (a.message_type === 'audio') return Mic;
  return FileText;
}

const HUMOR = {
  positivo: ['Satisfeito', 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30'],
  neutro: ['Neutro', 'text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-800'],
  negativo: ['Insatisfeito', 'text-amber-800 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30'],
  irritado: ['Irritado', 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30'],
};
const URG = { baixa: 'baixa', media: 'média', alta: 'alta' };

function SecaoIA({ conv, ia, onEtiquetas, onAtualizado }) {
  const [carregando, setCarregando] = useState(null);
  const a = conv.ia_analise;
  const r = conv.ia_resumo;
  const rodar = async (tipo) => {
    setCarregando(tipo);
    try {
      if (tipo === 'analise') await whatsappAPI.iaAnalisar(conv.jid); else await whatsappAPI.iaResumo(conv.jid);
      onAtualizado();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Não foi possível usar a IA agora'); }
    finally { setCarregando(null); }
  };
  const faltam = (a?.etiquetas || []).filter(e => !(conv.etiquetas || []).includes(e.id));
  return (
    <Secao icon={Sparkles} titulo="Leitura da IA" testid="wa-painel-ia">
      {ia.analise && (a ? (
        <div className="space-y-1.5 text-[13px]" data-testid="wa-ia-analise">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${HUMOR[a.sentimento]?.[1] || ''}`}>{HUMOR[a.sentimento]?.[0] || a.sentimento}</span>
            <span className={`text-xs rounded-full px-2 py-0.5 ${a.urgencia === 'alta' ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-medium' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>urgência {URG[a.urgencia] || a.urgencia}</span>
            {a.assunto && <span className="text-xs text-slate-600 dark:text-slate-300">{a.assunto}</span>}
          </div>
          {a.intencao && <p className="text-slate-700 dark:text-slate-200">{a.intencao}</p>}
          {a.motivo && <p className="text-xs text-slate-500">{a.motivo}</p>}
          {a.setor_nome && a.setor_id !== conv.setor_id && <p className="text-xs text-slate-500">Setor sugerido: <b className="text-slate-700 dark:text-slate-200">{a.setor_nome}</b></p>}
          {faltam.length > 0 && (
            <button onClick={() => onEtiquetas([...(conv.etiquetas || []), ...faltam.map(e => e.id)])} data-testid="wa-ia-aplicar-etiquetas"
              className="text-xs text-violet-700 dark:text-violet-300 hover:underline">Aplicar etiquetas sugeridas: {faltam.map(e => e.nome).join(', ')}</button>
          )}
        </div>
      ) : <p className="text-[13px] text-slate-500">Ainda não analisada.</p>)}
      {ia.resumo && r?.texto && (
        <div className="mt-3 rounded-lg bg-violet-50/70 dark:bg-violet-950/30 px-3 py-2 text-[13px] whitespace-pre-wrap" data-testid="wa-ia-resumo">
          {r.texto}
          <p className="text-[11px] text-slate-500 mt-1">Resumo de {new Date(r.em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      )}
      <div className="flex gap-2 mt-3">
        {ia.analise && (
          <button onClick={() => rodar('analise')} disabled={!!carregando} data-testid="wa-ia-analisar"
            className="h-8 px-2.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50">
            {carregando === 'analise' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}{a ? 'Analisar de novo' : 'Analisar'}
          </button>
        )}
        {ia.resumo && (
          <button onClick={() => rodar('resumo')} disabled={!!carregando} data-testid="wa-ia-resumir"
            className="h-8 px-2.5 rounded-lg border text-xs font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50">
            {carregando === 'resumo' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{r?.texto ? 'Resumir de novo' : 'Resumir conversa'}
          </button>
        )}
      </div>
    </Secao>
  );
}

export default function PainelCliente({ conv, onClose, onPedirCadastro, recarregarEm, setor, overlay, ia, onEtiquetas }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [verCadastro, setVerCadastro] = useState(false);
  const [modalCliente, setModalCliente] = useState(null);   // 'cadastrar' | 'vincular'
  const [historico, setHistorico] = useState(null);         // id do atendimento aberto
  const [filtroArq, setFiltroArq] = useState('todos');
  const jidRef = useRef(conv.jid);
  jidRef.current = conv.jid;

  const carregar = useCallback(async () => {
    const jid = conv.jid;
    try {
      const { data: d } = await whatsappAPI.painel(jid);
      if (jidRef.current === jid) { setDados(d); setErro(false); }
    } catch (_) { if (jidRef.current === jid) setErro(true); }
  }, [conv.jid]);

  useEffect(() => { setDados(null); setFiltroArq('todos'); carregar(); }, [conv.jid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (recarregarEm) { const t = setTimeout(carregar, 600); return () => clearTimeout(t); } return undefined; }, [recarregarEm, carregar]);

  const c = dados?.conversa || conv;
  const cli = dados?.cliente;
  const st = c.atendimento_status || 'bot';
  const sit = situacaoInfo(c.situacao);
  // atalhos: projeto ativo mais recente (ou o único) e serviço em aberto (ou o único)
  const projs = dados?.projetos || [];
  const projetoPrincipal = projs.find(p => p.status === 'em_andamento') || projs.find(p => p.status === 'suspenso') || (projs.length === 1 ? projs[0] : null);
  const servs = dados?.financeiro_visivel ? (dados?.servicos || []) : [];
  const servicoPrincipal = servs.find(x => !['finalizado', 'cancelado'].includes(x.status)) || (servs.length === 1 ? servs[0] : null);
  const vincularSugerido = async (cs) => {
    try {
      await whatsappAPI.vincularCliente(c.jid, cs.id);
      toast.success(`Conversa vinculada a ${cs.nome_completo}`);
      carregar();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Não foi possível vincular'); }
  };
  const arquivos = (dados?.arquivos || []).filter(a => filtroArq === 'todos'
    || (filtroArq === 'midia' ? ['image', 'video', 'sticker'].includes(a.message_type) : !['image', 'video', 'sticker'].includes(a.message_type)));

  return (
    <aside className={`flex flex-col min-h-0 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 ${overlay ? 'absolute inset-y-0 right-0 z-30 w-[min(360px,100%)] shadow-2xl' : 'w-[340px]'}`}
      data-testid="wa-painel">
      <div className="h-12 px-4 flex items-center justify-between shrink-0">
        <span className="text-sm font-semibold">Ficha do contato</span>
        <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 flex items-center justify-center" aria-label="Fechar painel" data-testid="wa-painel-fechar">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* identidade */}
        <div className="px-5 pb-4">
          <p className="text-lg font-semibold leading-tight break-words">{cli?.nome_completo || c.cliente_nome || c.push_name || 'Sem nome'}</p>
          <div className="mt-2 space-y-1 text-[13px] text-slate-600 dark:text-slate-300">
            {ehLid(c) && !cli?.telefone
              ? <NumeroOculto conv={c} onSalvo={carregar} />
              : <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" />{cli?.telefone || telefoneConversa(c) || '—'}</p>}
            {cli?.cpf && <p className="flex items-center gap-2"><IdCard className="w-3.5 h-3.5 text-slate-400" />{fmtCpf(cli.cpf)}</p>}
            {(cli?.cidade || cli?.estado) && <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" />{[cli.cidade, cli.estado].filter(Boolean).join(' / ')}</p>}
          </div>
          {dados && (
            <p className="mt-2 text-xs text-slate-500">
              Primeiro contato em {data(dados.primeiro_contato)}, {dados.total_mensagens} mensagens trocadas
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {c.cliente_id ? (
              <>
                <button onClick={() => setVerCadastro(true)} data-testid="wa-painel-ver-cadastro"
                  className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-medium flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Ver cadastro
                </button>
                <Link to={`/clientes/${c.cliente_id}`} className={btnLink}>
                  <ExternalLink className="w-3.5 h-3.5" /> Abrir cliente
                </Link>
                {projetoPrincipal && (
                  <Link to={linkProjeto(projetoPrincipal)} className={btnLink} data-testid="wa-painel-ir-projeto"
                    title={`${projetoPrincipal.tipo_projeto || 'Projeto'}${projetoPrincipal.numero_interno ? ` nº ${projetoPrincipal.numero_interno}` : ''}`}>
                    <FolderKanban className="w-3.5 h-3.5" /> Ir ao projeto
                  </Link>
                )}
                {servicoPrincipal && (
                  <Link to={linkServico(servicoPrincipal)} className={btnLink} data-testid="wa-painel-ir-servico" title={servicoPrincipal.titulo}>
                    <Wallet className="w-3.5 h-3.5" /> Ir ao serviço
                  </Link>
                )}
              </>
            ) : (
              <>
                <button onClick={() => setModalCliente('cadastrar')} data-testid="wa-painel-cadastrar-cliente"
                  className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-medium flex items-center gap-1.5"
                  title="Você mesmo preenche os dados e cria o cliente">
                  <UserPlus className="w-3.5 h-3.5" /> Cadastrar cliente
                </button>
                <button onClick={() => setModalCliente('vincular')} className={btnLink} data-testid="wa-painel-vincular-cliente"
                  title="O contato já é cliente: ligar esta conversa ao cadastro dele">
                  <Link2 className="w-3.5 h-3.5" /> Já é cliente
                </button>
                {st !== 'encerrada' && !c.cadastro_pendente_id && (
                  <button onClick={onPedirCadastro} className={btnLink} data-testid="wa-painel-pedir-cadastro"
                    title="O assistente pergunta os dados ao contato pelo WhatsApp; depois você valida em Cadastros">
                    <ClipboardList className="w-3.5 h-3.5" /> Pedir ao contato
                  </button>
                )}
              </>
            )}
          </div>
          {!c.cliente_id && c.cadastro_pendente_id && (
            <p className="mt-2 text-xs text-violet-700 dark:text-violet-300">O contato enviou o cadastro: valide na aba Cadastros.</p>
          )}
          {!c.cliente_id && dados?.clientes_sugeridos?.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 p-2.5 space-y-1.5" data-testid="wa-painel-sugestoes">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                {numeroInformado(c) ? 'O número informado' : 'Este número'} está no cadastro de:
              </p>
              {dados.clientes_sugeridos.map(cs => (
                <div key={cs.id} className="flex items-center gap-2">
                  <span className="flex-1 min-w-0 text-[13px] font-medium truncate">{cs.nome_completo}</span>
                  <button onClick={() => vincularSugerido(cs)} data-testid={`wa-painel-vincular-${cs.id}`}
                    className="h-7 px-2.5 rounded-md bg-white dark:bg-slate-900 border text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800">
                    Vincular
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* atendimento atual */}
        {c.protocolo && st !== 'encerrada' && (
          <Secao icon={Clock} titulo="Atendimento atual" extra={fmtProtocolo(c.protocolo)} testid="wa-painel-atual">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
              <dt className="text-slate-500">Setor</dt>
              <dd>{setor ? <Chip cor={setor.cor}>{setor.nome}</Chip> : <span className="text-slate-400">sem setor</span>}</dd>
              {st === 'fila' && <><dt className="text-slate-500">Na fila há</dt><dd className="font-medium text-amber-700 dark:text-amber-400">{desde(c.fila_desde) || '—'}</dd></>}
              {st === 'bot' && <><dt className="text-slate-500">Situação</dt><dd>na triagem, com o assistente</dd></>}
              {st === 'em_atendimento' && (
                <>
                  <dt className="text-slate-500">Atendente</dt><dd className="font-medium">{c.atendente_nome || '—'}</dd>
                  <dt className="text-slate-500">Situação</dt><dd>{sit?.label || 'Em atendimento'}</dd>
                  <dt className="text-slate-500">Começou há</dt><dd>{desde(c.atendimento_inicio) || '—'}</dd>
                  {c.primeira_resposta_em && c.atendimento_inicio && (
                    <><dt className="text-slate-500">1ª resposta</dt><dd>em {duracao((Date.parse(c.primeira_resposta_em) - Date.parse(c.atendimento_inicio)) / 1000)}</dd></>
                  )}
                </>
              )}
            </dl>
          </Secao>
        )}

        {ia?.ativa && (ia.analise || ia.resumo) && (
          <SecaoIA conv={{ ...c, etiquetas: conv.etiquetas ?? c.etiquetas }} ia={ia} onEtiquetas={onEtiquetas || (() => {})} onAtualizado={carregar} />
        )}

        {!dados && !erro && <div className="px-5 py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {erro && <p className="px-5 py-4 text-sm text-red-600">Não foi possível carregar a ficha. <button className="underline" onClick={carregar}>Tentar de novo</button></p>}

        {dados && cli && (
          <Secao icon={FolderKanban} titulo="Projetos" extra={dados.projetos?.length || null} testid="wa-painel-projetos">
            {!dados.projetos?.length && <p className="text-[13px] text-slate-500">Nenhum projeto cadastrado.</p>}
            <div className="space-y-3">
              {dados.projetos?.map(p => {
                const ativo = ['em_andamento', 'suspenso'].includes(p.status);
                return (
                  <div key={p.id} className={ativo ? '' : 'opacity-60'} data-testid={`wa-painel-projeto-${p.id}`}>
                    <div className="flex items-baseline gap-2">
                      <Link to={linkProjeto(p)} className="text-[13px] font-semibold truncate flex-1 hover:underline" title="Abrir o projeto" data-testid={`wa-painel-link-projeto-${p.id}`}>
                        {p.tipo_projeto || 'Projeto'}{p.numero_interno ? ` nº ${p.numero_interno}` : ''}<ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5 opacity-50" />
                      </Link>
                      {p.status === 'suspenso' && <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">suspenso</span>}
                      {!ativo && <span className="text-[11px] text-slate-500">{p.status === 'arquivado' ? 'finalizado' : p.status}</span>}
                    </div>
                    {ativo && (
                      <>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {p.etapa_atual_nome || '—'}{p.etapa_pos ? `, etapa ${p.etapa_pos} de ${p.etapa_total}` : ''}
                          {p.etapa_desde ? ` desde ${data(p.etapa_desde, false)}` : ''}
                        </p>
                        <Etapas pos={p.etapa_pos} total={p.etapa_total} suspenso={p.status === 'suspenso'} />
                        <div className="flex items-center gap-3 mt-1.5 text-xs">
                          {p.valor_credito ? <span className="text-slate-600 dark:text-slate-300">Crédito {brl(p.valor_credito)}</span> : null}
                          {p.pendencias > 0 && <span className="text-amber-700 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{p.pendencias} pendência{p.pendencias > 1 ? 's' : ''}</span>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </Secao>
        )}

        {dados?.financeiro_visivel && cli && (
          <Secao icon={Wallet} titulo="Financeiro" extra={dados.servicos?.length || null} testid="wa-painel-financeiro">
            {!dados.servicos?.length && <p className="text-[13px] text-slate-500">Nenhum serviço lançado.</p>}
            <div className="space-y-2.5">
              {dados.servicos?.map(s => (
                <div key={s.id} className="text-[13px]">
                  <div className="flex items-baseline gap-2">
                    <Link to={linkServico(s)} className="font-medium truncate flex-1 hover:underline" title="Abrir o serviço" data-testid={`wa-painel-link-servico-${s.id}`}>
                      {s.titulo}<ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5 opacity-50" />
                    </Link>
                    <span className="text-slate-600 dark:text-slate-300 tabular-nums">{brl(s.valor_total)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {s.parcelas_total ? `${s.parcelas_pagas} de ${s.parcelas_total} parcelas pagas` : 'sem parcelas'}
                    {s.status === 'cancelado' ? ', cancelado' : s.status === 'finalizado' ? ', finalizado' : ''}
                  </p>
                  {s.atrasadas > 0 ? (
                    <p className="text-xs font-medium text-red-600 dark:text-red-400 mt-0.5">{s.atrasadas} parcela{s.atrasadas > 1 ? 's' : ''} vencida{s.atrasadas > 1 ? 's' : ''}, {brl(s.valor_aberto)} em aberto</p>
                  ) : s.proxima_parcela ? (
                    <p className="text-xs text-slate-500 mt-0.5">Próxima: {brl(s.proxima_parcela.valor)} em {data(s.proxima_parcela.vencimento)}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </Secao>
        )}

        {dados && (
          <Secao icon={History} titulo="Atendimentos anteriores" extra={dados.atendimentos.length || null} testid="wa-painel-historico">
            {!dados.atendimentos.length && <p className="text-[13px] text-slate-500">Este é o primeiro atendimento.</p>}
            <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-1.5 space-y-3">
              {dados.atendimentos.map((a, i) => (
                <li key={`${a.protocolo}-${i}`} className="pl-4 relative">
                  <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900 ${a.modo === 'arquivado' ? 'bg-slate-400' : 'bg-emerald-600'}`} />
                  <p className="text-[13px] flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium">{data(a.fim)}</span> <span className="text-slate-500">{fmtProtocolo(a.protocolo)}</span>
                    {a.id && (
                      <button onClick={() => setHistorico(a.id)} data-testid={`wa-painel-hist-${a.id}`}
                        className="text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline">ver conversa</button>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {a.atendente_nome || 'sem atendente'}{a.setor_nome ? `, ${a.setor_nome}` : ''}
                    {duracao(a.duracao_seg ?? (a.duracao_min != null ? a.duracao_min * 60 : null)) ? `, ${duracao(a.duracao_seg ?? a.duracao_min * 60)}` : ''}
                    {a.modo === 'arquivado' ? ', arquivado' : ''}
                  </p>
                  {a.resumo_ia && (
                    <details className="mt-1">
                      <summary className="text-xs text-violet-700 dark:text-violet-300 cursor-pointer">Resumo da IA</summary>
                      <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap mt-1">{a.resumo_ia}</p>
                    </details>
                  )}
                  {a.avaliacao && (
                    <p className="flex gap-0.5 mt-0.5" aria-label={`Avaliação ${a.avaliacao} de 5`}>
                      {[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-3 h-3 ${n <= a.avaliacao ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </Secao>
        )}

        {dados?.notas?.length > 0 && (
          <Secao icon={StickyNote} titulo="Notas internas" extra={dados.notas.length} testid="wa-painel-notas">
            <div className="space-y-2">
              {dados.notas.slice(0, 5).map(n => (
                <div key={n.id} className="text-[13px] border-l-2 border-amber-400 pl-2.5">
                  <p className="whitespace-pre-wrap break-words line-clamp-4">{n.text}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{n.sent_by_name}, {data(n.timestamp)}</p>
                </div>
              ))}
            </div>
          </Secao>
        )}

        {dados && (
          <Secao icon={Paperclip} titulo="Arquivos" extra={dados.arquivos.length || null} testid="wa-painel-arquivos">
            {!dados.arquivos.length ? <p className="text-[13px] text-slate-500">Nenhum arquivo trocado.</p> : (
              <>
                <div className="flex gap-1 mb-2.5 text-xs">
                  {[['todos', 'Todos'], ['midia', 'Fotos e vídeos'], ['docs', 'Documentos e áudios']].map(([k, l]) => (
                    <button key={k} onClick={() => setFiltroArq(k)}
                      className={`h-7 px-2.5 rounded-md ${filtroArq === k ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>{l}</button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {arquivos.filter(a => a.message_type === 'image' || a.message_type === 'sticker').map(a => (
                    <a key={a.id} href={mediaUrl(a.id)} target="_blank" rel="noopener noreferrer" title={data(a.timestamp)}
                      className="aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 block">
                      <img src={mediaUrl(a.id)} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
                <div className="mt-1.5 space-y-1">
                  {arquivos.filter(a => !['image', 'sticker'].includes(a.message_type)).map(a => {
                    const Ic = iconeArquivo(a);
                    return (
                      <div key={a.id} className="flex items-center gap-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                        <a href={mediaUrl(a.id)} target="_blank" rel="noopener noreferrer" title="Abrir"
                          className="flex items-center gap-2.5 px-2 py-1.5 text-[13px] flex-1 min-w-0">
                          <Ic className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="truncate flex-1">{a.media_filename || ({ video: 'Vídeo', audio: 'Áudio' }[a.message_type] || 'Arquivo')}</span>
                          <span className="text-[11px] text-slate-400 shrink-0">{data(a.timestamp, false)}</span>
                        </a>
                        <a href={`${mediaUrl(a.id)}&baixar=1`} title="Baixar" aria-label="Baixar arquivo" data-testid={`wa-painel-baixar-${a.id}`}
                          className="h-7 w-7 mr-1 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 shrink-0">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Secao>
        )}
      </div>
      {historico && <HistoricoModal atId={historico} onClose={() => setHistorico(null)} />}
      {modalCliente && (
        <ContatoClienteModal conv={c} abaInicial={modalCliente} onClose={() => setModalCliente(null)} onFeito={carregar} />
      )}
      {verCadastro && <ClientQuickViewModal clientId={c.cliente_id} onClose={() => setVerCadastro(false)} />}
    </aside>
  );
}

/* Contato com número oculto pelo WhatsApp (LID): mostra o número informado ou permite informar. */
function NumeroOculto({ conv, onSalvo }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const informado = numeroInformado(conv);
  const salvar = async (e) => {
    e?.preventDefault();
    setSalvando(true);
    try {
      await whatsappAPI.informarNumero(conv.jid, valor);
      toast.success(valor.trim() ? 'Número salvo' : 'Número removido');
      setEditando(false);
      onSalvo?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Não foi possível salvar'); }
    finally { setSalvando(false); }
  };
  if (editando) {
    return (
      <form onSubmit={salvar} className="flex items-center gap-1.5" data-testid="wa-numero-form">
        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <input value={valor} onChange={e => setValor(e.target.value)} autoFocus placeholder="65 99999-9999" inputMode="tel"
          className="h-7 flex-1 min-w-0 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] outline-none focus:ring-2 focus:ring-emerald-500/40"
          data-testid="wa-numero-input" />
        <button type="submit" disabled={salvando} className="h-7 w-7 rounded-md bg-emerald-600 text-white flex items-center justify-center disabled:opacity-50" aria-label="Salvar número">
          {salvando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        <button type="button" onClick={() => setEditando(false)} className="h-7 w-7 rounded-md border flex items-center justify-center text-slate-500" aria-label="Cancelar">
          <X className="w-3.5 h-3.5" />
        </button>
      </form>
    );
  }
  return (
    <p className="flex items-center gap-2 flex-wrap">
      <Phone className="w-3.5 h-3.5 text-slate-400" />
      {informado ? (
        <>
          <span>{telefoneConversa(conv)}</span>
          <span className="text-[11px] px-1.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500"
            title="O WhatsApp esconde o número deste contato. Este número foi informado pelo contato ou pela equipe e não é verificado.">
            informado{conv.numero_informado === 'contato' ? ' pelo contato' : ''}
          </span>
        </>
      ) : (
        <span className="text-slate-500" title="O WhatsApp esconde o número de alguns contatos (privacidade). Ele aparece se o WhatsApp enviar junto com a mensagem, ou você pode informar.">
          número oculto pelo WhatsApp
        </span>
      )}
      <button onClick={() => { setValor(informado ? telefoneConversa(conv) : ''); setEditando(true); }} data-testid="wa-numero-editar"
        className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-1">
        <Pencil className="w-3 h-3" />{informado ? 'corrigir' : 'informar'}
      </button>
    </p>
  );
}
